/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { IPriceComponentResources, ISwatchItem, ProductComponentSwatchComponent, RatingComponent } from '@msdyn365-commerce/components';
import {
    getCatalogId,
    getUrlSync,
    IAny,
    IComponent,
    IComponentProps,
    ICoreContext,
    IGeneric,
    IGridSettings,
    IImageData,
    IImageSettings,
    Image,
    msdyn365Commerce
} from '@msdyn365-commerce/core';
import { AttributeSwatch, AttributeValue, Cart, CartLine, ProductDimension, ProductPrice } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    convertDimensionTypeToProductDimensionType,
    Dictionary,
    DimensionTypes,
    generateImageUrl,
    getProductPageUrlSync,
    IDimensionsApp,
    StringExtensions,
    validateCatalogId
} from '@msdyn365-commerce-modules/retail-actions';
import {
    format,
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import { reaction } from 'mobx';
import React, { useState } from 'react';
import { getCartState } from '@msdyn365-commerce/global-state';
import { addCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { IExtendedProductSearchResult } from '../../../../modules/hs-search-result-container/actions';
import { getAuthToken, getSelectedStoreId, getEventId, getCustomerId, getSelectedMenuId } from '../../../../utils/cookieutils';
import { INTERACTIVE_SUITE_CONTEXT_TIMEOUT_MS, waitForSuiteContext } from '../../../../utils/suitecontext';
import getCategoryMenuAsync, { GetCategoryMenuInput } from '../../../../actions/get-category-menu.action';
import { IGetActiveMenusResponse } from '../../../../custom-actions/DataServiceEntities.g';
import {
    addLineToCartPosData,
    removeLineByLineIdToCookie,
    removeLineFromCartPosData,
    removeLineFromUpdateCartPosData,
    syncCartLineToCookie
} from '../../../../utils/addUpdateCheckoutCookieUtil';
import lAC_RemoveCartLinesAsync, { RemoveCartLinesInput } from '../../../../actions/removeCartLine.action';
import { getCartLoadingState, setCartLoadingState, subscribeToCartLoading } from '../../../../utils/cartLoadingStateManager';
import { CART_LINE_QUANTITY_DEBOUNCE_MS, cartCarriesProperties } from '../../../../utils/cartlinequantity';

export interface IProductComponentProps extends IComponentProps<{ product?: IExtendedProductSearchResult; productPrice?: ProductPrice }> {
    className?: string;
    imageSettings?: IImageSettings;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    ratingAriaLabel?: string;
    ratingCountAriaLabel?: string;
    allowBack?: boolean;
    telemetryContent?: ITelemetryContent;
    quickViewButton?: React.ReactNode;
    productComparisonButton?: React.ReactNode;
    isEnabledProductDescription?: boolean;
    isPriceMinMaxEnabled?: boolean;
    priceResources?: IPriceComponentResources;
    inventoryLabel?: string;
    dimensionAvailabilities?: IProductsDimensionsAvailabilities[];
    swatchItemAriaLabel?: string;
}

export interface IProductComponent extends IComponent<IProductComponentProps> {}

const PriceComponentActions = {};

/**
 * Gets the product page url from the default swatch selected.
 * @param  productData - Product card to be rendered.
 * @returns The default color swatch selected if any.
 */
function getDefaultSwatchSelected(coreContext: ICoreContext, productData?: IExtendedProductSearchResult): AttributeSwatch | null {
    if (!productData || !productData.AttributeValues) {
        return null;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const attributeSwatches = productData.AttributeValues.find(
        attributeValue => attributeValue.KeyName?.toLocaleLowerCase() === dimensionToPreSelectInProductCard
    )?.Swatches;

    if (!ArrayExtensions.hasElements(attributeSwatches)) {
        return null;
    }

    const defaultSwatch = attributeSwatches.find(item => item.IsDefault === true) ?? attributeSwatches[0];
    return defaultSwatch;
}

/**
 * Gets the product image from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductImageUrlFromDefaultSwatch(coreContext: ICoreContext, productData?: IExtendedProductSearchResult): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    const swatchProductImageUrls = defaultSwatch?.ProductImageUrls;
    if (!ArrayExtensions.hasElements(swatchProductImageUrls)) {
        return productData?.PrimaryImageUrl;
    }

    return generateImageUrl(swatchProductImageUrls[0], coreContext.request.apiSettings);
}

/**
 * Updates the product url link to product details page.
 * @param  productDetailsPageUrl - Product page url.
 * @param  coreContext - Context of the module using the component.
 * @param  queryString - Querystring to be added to the URL.
 * @returns The update product page url.
 */
function updateProductUrl(productDetailsPageUrl: string, coreContext: ICoreContext, queryString: string): string {
    const sourceUrl = new URL(productDetailsPageUrl, coreContext.request.apiSettings.baseUrl);
    if (sourceUrl.search) {
        sourceUrl.search += `&${queryString}`;
    } else {
        sourceUrl.search += queryString;
    }

    const updatedUrl = new URL(sourceUrl.href);
    return updatedUrl.pathname + sourceUrl.search;
}

/**
 * Gets the react node for product unit of measure display.
 * @param  unitOfMeasure - DefaultUnitOfMeasure property from product.
 * @returns The node representing markup for unit of measure component.
 */
function renderProductUnitOfMeasure(unitOfMeasure?: string): JSX.Element | null {
    if (!unitOfMeasure) {
        return null;
    }
    return (
        <div className='msc-product__unit-of-measure'>
            <span>{unitOfMeasure}</span>
        </div>
    );
}

/**
 * Gets the react node for product availability.
 * @param inventoryAvailabilityLabel - The product information.
 * @returns The node representing markup for product availability.
 */
function renderProductAvailability(inventoryAvailabilityLabel: string | undefined): JSX.Element | null {
    if (!inventoryAvailabilityLabel || inventoryAvailabilityLabel === '') {
        return null;
    }

    return (
        <div className='msc-product__availability'>
            <span>{inventoryAvailabilityLabel}</span>
        </div>
    );
}

/**
 * Renders product comparison button similar to the quick view button.
 * @param productComparisonButton - React element of the button.
 * @param product - Current product info.
 * @param catalogId - Current catalog.
 * @returns React element for the specific product.
 */
function renderProductComparisonButton(
    productComparisonButton: React.ReactNode,
    product: IExtendedProductSearchResult,
    catalogId: number
): JSX.Element | undefined {
    validateCatalogId(catalogId);
    return React.cloneElement(productComparisonButton as React.ReactElement, { product, catalogId });
}

/**
 * Gets the product page url from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param productUrl - Product page url for the product card.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductPageUrlFromDefaultSwatch(
    coreContext: ICoreContext,
    productUrl: string,
    productData?: IExtendedProductSearchResult
): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    if (!defaultSwatch?.SwatchValue) {
        return productUrl;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const queryStringEncoded = encodeURIComponent(defaultSwatch.SwatchValue);
    const queryString = `${dimensionToPreSelectInProductCard}=${queryStringEncoded}`;
    return updateProductUrl(productUrl, coreContext, queryString);
}

const ProductCard: React.FC<IProductComponentProps> = ({
    data,
    context,
    imageSettings,
    savingsText,
    freePriceText,
    originalPriceText,
    currentPriceText,
    ratingAriaLabel,
    ratingCountAriaLabel,
    allowBack,
    typeName,
    id,
    telemetryContent,
    quickViewButton,
    productComparisonButton,
    isEnabledProductDescription,
    isPriceMinMaxEnabled,
    priceResources,
    inventoryLabel,
    dimensionAvailabilities,
    swatchItemAriaLabel
}) => {
    const product = data.product;
    const productPrice = data.productPrice;

    let productUrl = getProductPageUrlSync(product?.Name ?? '', product?.RecordId ?? Number.MIN_VALUE, context.actionContext, undefined);
    if (allowBack) {
        productUrl = updateProductUrl(productUrl, context, 'back=true');
    }
    const productImageUrlFromSwatch = getProductImageUrlFromDefaultSwatch(context, product) ?? product?.PrimaryImageUrl;
    const productPageUrlFromSwatch = getProductPageUrlFromDefaultSwatch(context, productUrl, product) ?? productUrl;
    const [productPageUrl, setProductPageUrl] = useState<string>(productPageUrlFromSwatch);
    const [productImageUrl, setProductImageUrl] = useState<string | undefined>(productImageUrlFromSwatch);
    React.useEffect(() => {
        setProductPageUrl(productPageUrlFromSwatch);
        setProductImageUrl(productImageUrlFromSwatch);
        updateQuantityFromCartState();
    }, [productUrl, productPageUrlFromSwatch, productImageUrlFromSwatch]);
    const [selectedSwatchItems] = useState(new Dictionary<DimensionTypes, ISwatchItem>());
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access -- app context is generic
    const enableStockCheck = context.app.config.enableStockCheck;

    const siteContext = context as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    /**
     * Updates the product page and Image url based on swatch selected.
     * @param coreContext - Context of the caller.
     * @param swatchItem - Dimension swatch selected.
     */
    const updatePageAndImageUrl = React.useCallback(
        (coreContext: ICoreContext, swatchItem: ISwatchItem) => {
            const dimensionType = swatchItem.dimensionType;
            selectedSwatchItems.setValue(dimensionType, swatchItem);
            if (StringExtensions.isNullOrWhitespace(swatchItem.value)) {
                return;
            }
            const queryString = `${dimensionType}=${swatchItem.value}`;
            let productPageUrlWithSwatch = '';
            if (productPageUrl.includes(dimensionType)) {
                const newUrl = new URL(productPageUrl, coreContext.request.apiSettings.baseUrl);
                newUrl.searchParams.delete(dimensionType);
                productPageUrlWithSwatch = updateProductUrl(newUrl.toString(), context, queryString);
            } else {
                productPageUrlWithSwatch = updateProductUrl(productPageUrl, context, queryString);
            }
            setProductPageUrl(productPageUrlWithSwatch);
            if (dimensionType === dimensionToPreSelectInProductCard) {
                const swatchProductImageUrl = ArrayExtensions.hasElements(swatchItem.productImageUrls)
                    ? swatchItem.productImageUrls[0]
                    : undefined;
                const newImageUrl = generateImageUrl(swatchProductImageUrl, coreContext.request.apiSettings);
                setProductImageUrl(newImageUrl);
            }
        },
        [selectedSwatchItems, context, productPageUrl]
    );

    if (!product) {
        return null;
    }

    /**
     * Checks if rendering the particular dimensions is allowed for product card.
     * @param dimensionType - Dimension to be displayed.
     * @returns Updates the state with new product page url.
     */
    function shouldDisplayDimension(dimensionType: string): boolean {
        const dimensionsContext = context as ICoreContext<IDimensionsApp>;
        const dimensionsToDisplayOnProductCard = dimensionsContext.app.config.dimensionsInProductCard;
        return (
            ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
            !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
            dimensionsToDisplayOnProductCard.includes(dimensionType.toLocaleLowerCase() as DimensionTypes)
        );
    }

    /**
     * Gets the react node for product dimension as swatch.
     * @param  attributeValues - Attribute value property from product.
     * @returns The node representing markup for unit of measure component.
     */
    function renderProductDimensions(attributeValues?: AttributeValue[]): JSX.Element | null {
        if (!attributeValues) {
            return null;
        }

        return (
            <div className='msc-product__dimensions'>
                {attributeValues.map((item: AttributeValue) => {
                    const dimensionTypeValue = item.KeyName?.toLocaleLowerCase() ?? '';
                    if (!shouldDisplayDimension(dimensionTypeValue)) {
                        return null;
                    }

                    const siteContext = context as ICoreContext<IDimensionsApp>;
                    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;
                    const dimensionType = dimensionTypeValue as DimensionTypes;
                    const swatches =
                        item.Swatches?.map<ISwatchItem>(swatchItem => {
                            return {
                                itemId: `${item.RecordId ?? ''}-${dimensionTypeValue}-${swatchItem.SwatchValue ?? ''}`,
                                value: swatchItem.SwatchValue ?? '',
                                dimensionType,
                                colorHexCode: swatchItem.SwatchColorHexCode,
                                imageUrl: swatchItem.SwatchImageUrl,
                                productImageUrls: swatchItem.ProductImageUrls,
                                isDefault: swatchItem.IsDefault,
                                swatchItemAriaLabel: swatchItemAriaLabel ? format(swatchItemAriaLabel, dimensionType) : '',
                                isDisabled:
                                    enableStockCheck &&
                                    dimensionAvailabilities?.find(
                                        dimensionAvailability => dimensionAvailability.value === (swatchItem.SwatchValue ?? '')
                                    )?.isDisabled
                            };
                        }) ?? [];
                    if (
                        dimensionType === dimensionToPreSelectInProductCard &&
                        ArrayExtensions.hasElements(swatches) &&
                        !swatches.some(swatch => swatch.isDefault)
                    ) {
                        swatches[0].isDefault = true;
                    }
                    return (
                        <ProductComponentSwatchComponent
                            context={context}
                            swatches={swatches}
                            onSelectDimension={updatePageAndImageUrl}
                            key={item.RecordId}
                        />
                    );
                })}
            </div>
        );
    }

    function renderQuickView(quickview: React.ReactNode, item?: number): JSX.Element | undefined {
        if (quickview === null) {
            return undefined;
        }
        const selectedDimensions: ProductDimension[] = selectedSwatchItems.getValues().map<ProductDimension>(swatches => {
            return {
                DimensionTypeValue: convertDimensionTypeToProductDimensionType(swatches.dimensionType),
                DimensionValue: {
                    RecordId: 0,
                    Value: swatches.value
                }
            };
        });
        return React.cloneElement(quickview as React.ReactElement, { selectedProductId: item, selectedDimensions });
    }

    // Construct telemetry attribute to render
    const payLoad = getPayloadObject('click', telemetryContent!, '', product.RecordId.toString());

    const attribute = getTelemetryAttributes(telemetryContent!, payLoad);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- -- Do not need type check for appsettings
    const isUnitOfMeasureEnabled = context.app.config && context.app.config.unitOfMeasureDisplayType === 'buyboxAndBrowse';
    const [quantity, setQuantity] = useState<number>(0);
    const [isMenuCompatible, setIsMenuCompatible] = useState<boolean>(true);
    const [activeMenus, setActiveMenus] = useState<IGetActiveMenusResponse>({});
    const [isCartUpdating, setIsCartUpdating] = useState<boolean>(getCartLoadingState());
    // Variant items can't be added from the listing card - the exact variant (and its InventoryDimensionId)
    // is only resolved via the selector on the product details page.
    const hasVariants = (product.Variants ?? []).length > 0;

    React.useEffect(() => {
        const unsubscribe = subscribeToCartLoading((loading: boolean) => {
            setIsCartUpdating(loading);
        });
        return unsubscribe;
    }, []);

    const useDebouncedCartUpdater = () => {
        const debounceTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);

        const debouncedAddUpdateCart = (quantity: number) => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }

            debounceTimeout.current = setTimeout(() => {
                void addUpdateCart(quantity);
            }, CART_LINE_QUANTITY_DEBOUNCE_MS);
        };

        return debouncedAddUpdateCart;
    };
    const debouncedAddUpdateCart = useDebouncedCartUpdater();

    const handleDecrease = (): void => {
        if (hasVariants) {
            return;
        }
        const updatedQuantity = quantity > 0 ? quantity - 1 : 0;
        setQuantity(updatedQuantity);
        debouncedAddUpdateCart(updatedQuantity);
    };

    const handleIncrease = (): void => {
        if (hasVariants) {
            return;
        }
        const updatedQuantity = quantity < 10 ? quantity + 1 : 10;
        setQuantity(updatedQuantity);
        debouncedAddUpdateCart(updatedQuantity);
    };
    const updateQuantityFromCartState = React.useCallback(async (): Promise<void> => {
        const cartState = await getCartState(context.actionContext);
        const matchingLines = cartState?.cart?.CartLines?.filter((cartline: CartLine) => cartline.ItemId === product.ItemId) ?? [];
        if (hasVariants) {
            // Every variant of this item-master shares one ItemId but is its own cart line (distinct
            // ProductId/InventoryDimensionId) - the card shows the parent's total quantity across all of them.
            const totalQuantity = matchingLines.reduce((sum, line) => sum + (line.Quantity ?? 0), 0);
            setQuantity(totalQuantity);
            return;
        }
        setQuantity(matchingLines[0]?.Quantity ?? 0);
    }, [context.actionContext, product.ItemId, hasVariants]);

    React.useEffect(() => {
        let disposeCartReaction: (() => void) | undefined;
        let isDisposed = false;

        const setupCartReaction = async (): Promise<void> => {
            const cartState = await getCartState(context.actionContext);
            if (isDisposed || !cartState) {
                return;
            }

            disposeCartReaction = reaction(
                () => cartState.cart?.CartLines?.map(line => `${line.LineId}-${line.Quantity}`).join('|') ?? '',
                () => {
                    void updateQuantityFromCartState();
                }
            );

            await updateQuantityFromCartState();
        };

        void setupCartReaction();

        return () => {
            isDisposed = true;
            if (disposeCartReaction) {
                disposeCartReaction();
            }
        };
    }, [context.actionContext, updateQuantityFromCartState]);

    const fetchActiveMenus = async () => {
        const eventId = getEventId();
        if (!eventId) {
            return;
        }

        const channelId = parseInt(getSelectedStoreId() || '0', 10);
        const menuPriceSearchArgs = { CustomerId: getCustomerId() || '', IncludeMenuItemPrices: true };
        const getCategoryMenuInput = new GetCategoryMenuInput(eventId, menuPriceSearchArgs, channelId, true, false, '', true);
        try {
            const result = await getCategoryMenuAsync(getCategoryMenuInput, context.actionContext);
            if (result) {
                setActiveMenus(result);
            }
        } catch (error) {
            console.log('Error fetching active menus:', error);
        }
    };

    const checkMenuCompatibility = async () => {
        try {
            const cartState = await getCartState(context.actionContext);
            const productData = await product;

            if (!activeMenus?.Menus || activeMenus.Menus.length === 0) {
                setIsMenuCompatible(true);
                return;
            }

            const activeMenuIds = activeMenus.Menus.map(menu => menu.Id).filter(Boolean);
            const currentMenuId = getSelectedMenuId();

            if (cartState?.cart?.CartLines) {
                const thisProductCartLine = cartState.cart.CartLines.find(cartLine => cartLine.ItemId === productData.ItemId);

                if (thisProductCartLine) {
                    const menuIdProperty = thisProductCartLine.ExtensionProperties?.find(prop => prop.Key === 'ITSMenuId');
                    const cartLineMenuId = menuIdProperty?.Value?.StringValue;

                    if (cartLineMenuId) {
                        const isCompatible = activeMenuIds.includes(cartLineMenuId);
                        setIsMenuCompatible(isCompatible);
                        return;
                    }
                }
            }

            setIsMenuCompatible(activeMenuIds.includes(currentMenuId || ''));
        } catch (error) {
            console.log('Error checking menu compatibility:', error);
            setIsMenuCompatible(true);
        }
    };

    React.useEffect(() => {
        void fetchActiveMenus();
    }, []);

    React.useEffect(() => {
        void checkMenuCompatibility();
    }, [activeMenus]);

    React.useEffect(() => {
        void checkMenuCompatibility();
    }, [quantity]);

    const addUpdateCart = async (currentQuantity: number) => {
        setCartLoadingState(true);
        try {
            const cartState = await getCartState(context.actionContext);
            const cartLines = cartState?.cart?.CartLines ?? [];
            const cartId = cartState?.cart?.Id;

            const targetProductId = (await product).ItemId;

            if (cartLines.length > 0) {
                for (const cartLine of cartLines) {
                    if (cartLine.ItemId === targetProductId) {
                        const updatedCartLine: CartLine = {
                            ...cartLine,
                            Quantity: currentQuantity
                        };

                        if (currentQuantity === 0) {
                            const lineId = cartLine.LineId?.toString();
                            if (lineId) {
                                const customerId = getCustomerId() || '';
                                const updatedCartAfterRemoval = await lAC_RemoveCartLinesAsync(
                                    new RemoveCartLinesInput(cartId, customerId, [lineId], getAuthToken()),
                                    context.actionContext
                                );
                                if (updatedCartAfterRemoval) {
                                    await cartState.updateCart({ newCartObject: updatedCartAfterRemoval });
                                    // Remove from cookie as well
                                    removeLineFromCartPosData(cartLine.ProductId);
                                    removeLineFromUpdateCartPosData(cartLine.ProductId);
                                    removeLineByLineIdToCookie(cartLine.LineId);
                                }
                            }
                        } else {
                            const updateResult = await cartState.updateCartLines({ updatedCartLinesObject: [updatedCartLine] });
                            if (updateResult.status !== 'SUCCESS') {
                                await updateQuantityFromCartState();
                                return;
                            }
                            await updateCartWithExtensionProperties(cartId, cartState.cart, true);
                            // Update cookie with new quantity
                            const updateCartLineData = {
                                ItemId: updatedCartLine.ItemId,
                                ProductId: updatedCartLine.ProductId,
                                Quantity: currentQuantity,
                                UnitOfMeasureSymbol: updatedCartLine.UnitOfMeasureSymbol,
                                Comments: updatedCartLine.Comment,
                                ExtensionProperties: updatedCartLine.ExtensionProperties || [],
                                LineId: updatedCartLine.LineId
                            };
                            syncCartLineToCookie(updateCartLineData);
                        }
                        return;
                    }
                }
            }

            // If we reached here, either no cart lines matched or cart was empty — add new product
            const productData = await product;
            const selectedMenuId = getSelectedMenuId();
            // Create extension properties with menu ID for cart line
            const extensionProperties = [
                ...(productData.ExtensionProperties || []),
                {
                    Key: 'ITSMenuId',
                    Value: {
                        StringValue: selectedMenuId || ''
                    }
                }
            ];

            const newCartLine: CartLine = {
                Quantity: currentQuantity,
                ItemId: productData.ItemId,
                Price: productData.Price,
                ExtensionProperties: extensionProperties
            };

            const updatedCart = await addCartLinesAsync({ callerContext: context.actionContext }, cartId, [newCartLine]);
            if (!(await updateCartWithExtensionProperties(cartId, updatedCart, true))) {
                await (await getCartState(context.actionContext)).refreshCart({});
            }

            const addedCartLine = updatedCart?.CartLines?.find(line => line.ItemId === productData.ItemId) ?? undefined;
            const ProductId = addedCartLine?.ProductId ?? productData.RecordId;

            // Add to cookie
            addLineToCartPosData({
                ItemId: productData.ItemId,
                ProductId: ProductId,
                Quantity: currentQuantity,
                UnitOfMeasureSymbol: productData.DefaultUnitOfMeasure,
                Comments: '',
                ExtensionProperties: extensionProperties
            });
        } catch (error) {
            console.error('Error updating cart:', error);
        } finally {
            setCartLoadingState(false);
        }
    };

    /** Stamps the cart with its suite and event, or only where it lacks them; returns whether it saved the cart. */
    const updateCartWithExtensionProperties = async (
        cartId: string,
        fetchedCart?: Cart,
        onlyWhereMissing: boolean = false
    ): Promise<boolean> => {
        const suiteContext = await waitForSuiteContext(INTERACTIVE_SUITE_CONTEXT_TIMEOUT_MS);

        if (!suiteContext) {
            console.log('LTS Info', 'product.component: suite context never resolved, cart not stamped', { cartId });
            return false;
        }

        const selectedStoreId = suiteContext.channelId;
        const eventId = getEventId() || '';
        const tableId = suiteContext.tableId;
        const customerId = getCustomerId() || '';

        try {
            const cartState = await getCartState(context.actionContext);

            const updatedCart = {
                ...fetchedCart,
                Id: cartId,
                customerId: customerId,
                ExtensionProperties: [
                    {
                        Key: 'ITS_TableEventId',
                        Value: {
                            StringValue: eventId
                        }
                    },
                    {
                        Key: 'LS_TableAssigned',
                        Value: {
                            StringValue: tableId
                        }
                    },
                    {
                        Key: 'ITSIntegrationChannelId',
                        Value: {
                            LongValue: parseInt(selectedStoreId, 10)
                        }
                    },
                    {
                        Key: 'ITSCustomerId',
                        Value: {
                            StringValue: customerId
                        }
                    }
                ]
            };

            if (onlyWhereMissing && cartCarriesProperties(fetchedCart, updatedCart.ExtensionProperties)) {
                return false;
            }

            await cartState.updateCart({ newCartObject: updatedCart });
            return true;
        } catch (error) {
            console.error('Error', error);
            return false;
        }
    };
    const url = getUrlSync('product', context?.actionContext, {
        category: {
            Url: `product-details/${product.ItemId}`,
            Slug: `product-details/${product.ItemId}`
        }
    });

    return (
        <>
            {isEnabledProductDescription ? (
                <>
                    <div className='msc-product__image__description'>
                        <div className='msc-product__image'>
                            {renderProductPlacementImage(
                                imageSettings,
                                context.request.gridSettings,
                                productImageUrl,
                                product.PrimaryImageUrl,
                                product.Name,
                                context
                            )}
                        </div>
                        <div className='msc-product__title_description'>
                            <h2 className='msc-product__title__text'>{product.Name}</h2>
                            {renderPrice(
                                context,
                                typeName,
                                id,
                                product,
                                savingsText,
                                freePriceText,
                                originalPriceText,
                                currentPriceText,
                                isPriceMinMaxEnabled,
                                priceResources,
                                productPrice
                            )}
                            {isUnitOfMeasureEnabled && renderProductUnitOfMeasure(product.DefaultUnitOfMeasure)}
                            {renderDescription(product.Description)}
                        </div>
                    </div>
                    <a
                        href={`${context.request.sitePath || ''}/product-details?item=${product.ItemId}`}
                        onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                        aria-label={renderLabel(
                            product.Name,
                            context.cultureFormatter.formatCurrency(product.Price),
                            product.AverageRating,
                            ratingAriaLabel,
                            product.TotalRatings,
                            ratingCountAriaLabel
                        )}
                        className='msc-product'
                        {...attribute}
                    ></a>
                </>
            ) : (
                <>
                    <div className='msc-product-container'>
                        <div className='msc-product-quantity__wrapper'>
                            <div className='msc-product-details-wrapper'>
                                <div className='msc-product__details'>
                                    <h2 className='msc-product__title'>{product.Name}</h2>
                                    {renderDescription(product.Description)}
                                    <div>
                                        <a
                                            href={url}
                                            onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                                            aria-label={renderLabel(
                                                product.Name,
                                                context.cultureFormatter.formatCurrency(product.Price),
                                                product.AverageRating,
                                                ratingAriaLabel
                                            )}
                                            className='msc-product__link'
                                            {...attribute}
                                        >
                                            View more
                                        </a>
                                    </div>
                                </div>
                                <div className='msc-product__image'>
                                    {renderProductPlacementImage(
                                        imageSettings,
                                        context.request.gridSettings,
                                        productImageUrl,
                                        product.PrimaryImageUrl,
                                        product.Name,
                                        context
                                    )}
                                </div>
                            </div>

                            <div className='msc-quantity-selector'>
                                {renderPrice(
                                    context,
                                    typeName,
                                    id,
                                    product,
                                    savingsText,
                                    freePriceText,
                                    originalPriceText,
                                    currentPriceText,
                                    isPriceMinMaxEnabled,
                                    priceResources,
                                    productPrice
                                )}
                                {isUnitOfMeasureEnabled && renderProductUnitOfMeasure(product.DefaultUnitOfMeasure)}

                                <div className='msc-quantity-selector-container'>
                                    <button
                                        className={`quantity-selector-btn ${
                                            hasVariants || quantity === 0 || !isMenuCompatible || isCartUpdating
                                                ? 'quantity-selector-btn__disabled'
                                                : 'quantity-selector-btn__enabled'
                                        }`}
                                        onClick={handleDecrease}
                                        disabled={hasVariants || quantity === 0 || !isMenuCompatible || isCartUpdating}
                                    >
                                        -
                                    </button>
                                    <span className='quantity-selector-value'>{quantity}</span>
                                    <button
                                        className={`quantity-selector-btn ${
                                            hasVariants || quantity >= 10 || !isMenuCompatible || isCartUpdating
                                                ? 'quantity-selector-btn__disabled'
                                                : 'quantity-selector-btn__enabled'
                                        }`}
                                        onClick={handleIncrease}
                                        disabled={hasVariants || quantity >= 10 || !isMenuCompatible || isCartUpdating}
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
            {renderProductDimensions(product.AttributeValues)}
            {!context.app.config.hideRating &&
                renderRating(context, typeName, id, product.AverageRating, product.TotalRatings, ratingAriaLabel, ratingCountAriaLabel)}
            {renderProductAvailability(inventoryLabel)}
            {quickViewButton && renderQuickView(quickViewButton, product.RecordId)}
            {productComparisonButton && renderProductComparisonButton(productComparisonButton, product, getCatalogId(context.request))}
        </>
    );
};

function renderLabel(
    name?: string,
    price?: string,
    rating?: number,
    ratingAriaLabelText?: string,
    reviewCount?: number,
    ratingCountAriaLabelText?: string
): string {
    const reviewCountArialableText = getReviewAriaLabel(reviewCount, ratingCountAriaLabelText ?? '');
    return `${name ?? ''} ${price ?? ''} ${getRatingAriaLabel(rating, ratingAriaLabelText)}${
        reviewCountArialableText ? ` ${reviewCountArialableText}` : ''
    }`;
}

function renderDescription(description?: string): JSX.Element | null {
    return <p className='msc-product__description'>{description}</p>;
}

function getRatingAriaLabel(rating?: number, ratingAriaLabel?: string): string {
    if (rating && ratingAriaLabel) {
        const roundedRating = rating.toFixed(2);
        return format(ratingAriaLabel || '', roundedRating, '5');
    }
    return '';
}

function getReviewAriaLabel(reviewCount?: number, ratingCountAriaLabelText?: string): string {
    if (reviewCount && ratingCountAriaLabelText) {
        return format(ratingCountAriaLabelText || '', reviewCount);
    }
    return '';
}

function renderRating(
    coreContext: ICoreContext,
    moduleTypeName: string,
    moduleId: string,
    avgRating?: number,
    totalRatings?: number,
    ariaLabel?: string,
    ratingCountAriaLabel?: string
): JSX.Element | null {
    if (!avgRating) {
        return null;
    }

    const numberRatings = totalRatings?.toString() || undefined;
    const ratingAriaLabelText = getRatingAriaLabel(avgRating, ariaLabel);
    const ratingCountAriaLabelText = getReviewAriaLabel(Number(numberRatings), ratingCountAriaLabel);

    return (
        <RatingComponent
            context={coreContext}
            id={moduleId}
            typeName={moduleTypeName}
            avgRating={avgRating}
            ratingCount={numberRatings}
            readOnly
            ariaLabel={ratingAriaLabelText}
            ratingCountAriaLabel={ratingCountAriaLabelText}
            data={{}}
        />
    );
}

function renderPrice(
    context: ICoreContext,
    typeName: string,
    id: string,
    productSearchResult: IExtendedProductSearchResult,
    savingsText?: string,
    freePriceText?: string,
    originalPriceText?: string,
    currentPriceText?: string,
    isPriceMinMaxEnabled?: boolean,
    priceResources?: IPriceComponentResources,
    productPrice?: ProductPrice
): JSX.Element | null {
    const actualPrice = productSearchResult.Price ?? 0;
    const originalPrice = productSearchResult.MenuItemOriginalPrice ?? 0;
    const hasDiscount = (productSearchResult.MenuItemDiscount ?? 0) > 0;

    return (
        <div className='hs-product-price'>
            <span className='hs-product-price__actual'>{context.cultureFormatter.formatCurrency(actualPrice)}</span>
            {hasDiscount && <span className='hs-product-price__original'>{context.cultureFormatter.formatCurrency(originalPrice)}</span>}
        </div>
    );
}

function renderProductPlacementImage(
    imageSettings?: IImageSettings,
    gridSettings?: IGridSettings,
    imageUrl?: string,
    fallbackImageUrl?: string,
    altText?: string,
    context?: ICoreContext<IGeneric<IAny>>
): JSX.Element | null {
    if (!imageUrl || !gridSettings || !imageSettings) {
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Site level config can be of any type.
    const contextConfig = context?.app.config?.placeholderImageName;
    const emptyPlaceHolderImage = contextConfig as string;
    let fallbackImageSource = fallbackImageUrl;
    if (emptyPlaceHolderImage && fallbackImageUrl) {
        fallbackImageSource = `${fallbackImageUrl},${emptyPlaceHolderImage}`;
    }
    const img: IImageData = {
        src: imageUrl,
        altText: altText ? altText : '',
        fallBackSrc: fallbackImageSource
    };
    const imageProps = {
        gridSettings,
        imageSettings
    };
    imageProps.imageSettings.cropFocalRegion = true;
    return (
        <Image
            requestContext={context?.actionContext.requestContext}
            {...img}
            {...imageProps}
            loadFailureBehavior='empty'
            bypassHideOnFailure
        />
    );
}

export const ProductComponent: React.FunctionComponent<IProductComponentProps> = msdyn365Commerce.createComponentOverride<
    IProductComponent
>('Product', { component: ProductCard, ...PriceComponentActions });

export default ProductComponent;
